<template>
  <div class="slider-wrapper">
    <div
        class="slider-root"
        role="slider"
        :aria-valuenow="selectedIndex"
        :aria-valuemin="0"
        :aria-valuemax="steps.length - 1"
        tabindex="0"
        ref="root"
        @keydown.left.prevent="keyStep(-1)"
        @keydown.right.prevent="keyStep(1)"
        @mouseleave="endDrag"
    >
      <!-- Floating output -->
      <div
          class="output-floating"
          :style="{ left: thumbPercent + '%', transform: 'translateX(-50%)' }"
      >
        <output class="slider-output">{{ currentLabel }}</output>
      </div>

      <!-- Track -->
      <div
          class="slider-track"
          data-orientation="horizontal"
          ref="track"
          @click="onTrackClick"
          @mousedown="startDrag"
          @touchstart.prevent="startDrag"
      >
        <div
            class="slider-active"
            :style="{ width: thumbPercent + '%' }"
        ></div>

        <div class="slider-marks">
          <div
              v-for="(step, idx) in steps"
              :key="idx"
              class="slider-mark"
              :class="{ 'mark-active': idx <= selectedIndex }"
              :style="{ left: markPercent(idx) + '%' }"
              @click.stop="_commit(idx)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "StepSlider",
  props: {
    steps: { type: Array, required: true },
    value: { type: [String, Number], default: null },
    formatLabel: { type: Function, default: null },
  },

  data() {
    return {
      selectedIndex: this._findIndex(this.value),
      isDragging: false,
    };
  },

  watch: {
    value(val) {
      this.selectedIndex = this._findIndex(val);
    },
  },

  computed: {
    thumbPercent() {
      if (this.steps.length <= 1) return 0;
      return (this.selectedIndex / (this.steps.length - 1)) * 100;
    },

    currentLabel() {
      const step = this.steps[this.selectedIndex];
      if (!step) return "";

      if (this.formatLabel) return this.formatLabel(step);

      return step.label !== undefined
          ? step.label
          : step.unique_id !== undefined
              ? step.unique_id
              : step;
    },
  },

  methods: {
    _findIndex(val) {
      if (val === null || val === undefined) return 0;

      const idx = this.steps.findIndex(
          (s) => s === val || s.unique_id === val || s.label === val
      );

      return idx !== -1 ? idx : 0;
    },

    markPercent(idx) {
      if (this.steps.length <= 1) return 0;
      return (idx / (this.steps.length - 1)) * 100;
    },

    _indexFromX(clientX) {
      const track = this.$refs.track;
      if (!track) return this.selectedIndex;

      const rect = track.getBoundingClientRect();

      let pct = (clientX - rect.left) / rect.width;
      pct = Math.max(0, Math.min(1, pct));

      return Math.round(pct * (this.steps.length - 1));
    },

    _commit(idx) {
      if (idx === this.selectedIndex) return;

      this.selectedIndex = idx;
      const step = this.steps[idx];
      const emitted =
          step && step.unique_id !== undefined ? step.unique_id : step;

      this.$emit("input", emitted);
      this.$emit("change", emitted);
    },

    onTrackClick(e) {
      this._commit(this._indexFromX(e.clientX));
    },

    startDrag(e) {
      this.isDragging = true;

      this.onDrag(e);

      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.endDrag);
      document.addEventListener("touchmove", this.onDrag, {
        passive: false,
      });
      document.addEventListener("touchend", this.endDrag);
    },

    onDrag(e) {
      if (!this.isDragging) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const idx = this._indexFromX(clientX);

      this._commit(idx);
    },

    endDrag() {
      this.isDragging = false;

      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.endDrag);
      document.removeEventListener("touchmove", this.onDrag);
      document.removeEventListener("touchend", this.endDrag);
    },

    keyStep(dir) {
      const ni = Math.max(
          0,
          Math.min(this.steps.length - 1, this.selectedIndex + dir)
      );

      this._commit(ni);
    },
  },

  beforeDestroy() {
    this.endDrag();
  },
};
</script>

<style scoped>
.slider-wrapper {
  width: 100%;
  padding: 28px 8px 16px;
  box-sizing: border-box;
  user-select: none;
}

.slider-root {
  position: relative;
  width: 100%;
  outline: none;
}

/* ── Floating output + thumb ── */
.output-floating {
  position: absolute;
  top: -30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
  pointer-events: none;
}

.slider-output {
  display: block;
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--btn-background, #fff);
  border: 1px solid var(--btn-background, #888);
  border-radius: 4px;
  padding: 2px 6px;
  margin-bottom: 4px;
  white-space: nowrap;
  line-height: 1.4;
  pointer-events: none;
}

/* ── Track ── */
.slider-track {
  position: relative;
  width: 100%;
  height: 6px;
  background: rgba(209, 213, 219, 0.28);
  border-radius: 3px;
  margin-top: 14px;
  cursor: pointer;
}

.slider-active {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--btn-background, #007bff);
  border-radius: 3px;
  pointer-events: none;
  transition: width 0.12s;
}

/* ── Marks ── */
.slider-marks {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.slider-mark {
  position: absolute;
  top: 50%;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--btn-background-second, #007bff);
  background: var(--bg-body, #1a1a1a);
  transform: translate(-50%, -50%);
  transition: background 0.15s, border-color 0.15s;
  cursor: pointer;
}

.slider-mark::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
}

.slider-mark.mark-active {
  background: var(--btn-background, #007bff);
  border-color: var(--btn-background-second, #007bff);
}
.slider-track {
  touch-action: none;
}
.slider-mark {
  transition: transform 0.15s, background 0.15s, border-color 0.15s;
}

.slider-mark.mark-active {
  transform: translate(-50%, -50%) scale(1.15);
}
</style>
